/**
 * @fileoverview Advanced query compilation and matching using filtrex.
 * Provides functions to compile and evaluate filter expressions against data items.
 * @module utils/jrquery
 */

// compileExpression is loaded globally via script tag in index.html
/* global compileExpression */


import { DEBUG_CBM, MAPAPP_DEFAULTS, TEXT_CONSTANTS, ADVANCED_SEARCH_FIELDS, FIELD_ISBOOKMARK, RESULT_MANAGER_CONSTANTS, SEARCH_CONSTANTS } from '../constants.js';
import { parseStringGetDirectiveDictionary, addErrorToStruct, addErrorToString, anyFieldMatches } from './jrfuncs.js';
import { convertFeetToNativeMercaturUnits, distanceBetweenTwoItems } from './jrgeofuncs.js';


// module global data that we can use for custom function caching
// caller should call clearCustomFunctionDataCache() before batch operations that need this data
let customFunctionDataCache = {};

/**
 * Clears the custom function data cache.
 * Should be called before batch search operations to ensure fresh cache state.
 */
export function clearCustomFunctionDataCache() {
    customFunctionDataCache = { };
}

/**
 * Stores an item in the cache for access by custom filter functions.
 * Used to pass the current item being evaluated to custom filtrex functions.
 * @param {Object} item - The item to cache
 */
export function cacheSetItem(item) {
    customFunctionDataCache.item = item;
}

/**
 * Retrieves the cached item for use by custom filter functions.
 * @returns {Object} The cached item, or undefined if not set
 */
export function cacheGetItem() {
    return customFunctionDataCache.item;
}



/**
 * Parses and processes query directives from a search pack.
 * Extracts directives like ;$limit=N, ;$region=XX, ;$random from the query string.
 * Modifies searchPack in place with parsed values and stripped query.
 * @param {Object} searchPack - The search pack containing the query
 * @param {string} searchPack.query - The query string to parse
 * @returns {string|null} Error message if parsing failed, null otherwise
 */
export function preProcessQueryParseOptionsDirectives(searchPack) {
    // Strip off directives
    // Special directives match the pattern ;$flag or ;$flag=val or ;$flag="quoted val" at end of query

    // parse off directives
    let {qstringNew, directiveDictionary, errorString} = parseStringGetDirectiveDictionary(searchPack.query);

    // Process known directives and store to options
    for (const [name, value] of Object.entries(directiveDictionary)) {
        if (name === 'limit') {
            if (value === 'all') {
                searchPack.limit = RESULT_MANAGER_CONSTANTS.NO_LIMIT_RESULT_COUNT;
            } else {
                const limitInt = parseInt(value, 10);
                if (limitInt<=0 || !limitInt) {
                    errorString = addErrorToString(errorString, 'limit value should be integer>0');
                } else {
                    searchPack.limit = limitInt;
                }
            }
        } else if (name === 'region') {
            // Alias: $reg -> jregion
            if (value) {
                searchPack.jregion = value.toUpperCase();
            } else {
                searchPack.jregion = value;
            }
        } else if (name === 'random') {
            searchPack.random = true;
        } else {
            // Unknown directive
            errorString = addErrorToString(errorString, `Unknown query directive: $${name}`);
        }
    }

    // we let searchPack tell us to override users limit which allows a "show all" feature even on explicit user limit
    if (searchPack.overrideLimit) {
        searchPack.limit = searchPack.overrideLimit;
    }

    // strip any trailing spaces and semicolons from queryNew and STORE it
    searchPack.query = qstringNew.replace(/[\s;]+$/, '');

    const error = errorString ? errorString : null;

    // store it
    searchPack.error = error;

    // return error
    return error;
}


/**
 * Preprocesses a search pack's query to determine if it uses advanced (filtrex) syntax.
 * Performs query massaging (e.g., replacing standalone '=' with '==').
 * Sets searchPack.isAdvanced and searchPack.query with the processed query.
 * @param {Object} searchPack - The search pack containing the query to analyze
 * @param {string} searchPack.query - The query string to check for advanced operators
 * @returns {string|null} Error message if processing failed, null otherwise
 */
export function preProcessQueryCheckAdvanced(searchPack) {
    // Check if query contains filtrex operators: comparisons, parentheses, or logical operators
    // Matches: ==, !=, ~=, <=, >=, <, >, and, or, not, parentheses
    let error = null;

    const qString = searchPack.query;

    const advancedPattern = /[<>=!()"]/i;
    const isAdvanced = advancedPattern.test(qString);
    //console.log(`ATTN: In preProcessQueryCheckAdvanced with isAdvanced = ${isAdvanced}`);

    // let's do some massaging to avoid common pitfalls

    // Replace standalone "=" with "==" (but not ==, !=, <=, >=, ~=)
    // Uses negative lookbehind for [!<>=~] and negative lookahead for [=]
    // ONLY in the code between quoted literals: this used to rewrite inside them too, so a search
    // for "a=b" looked for "a==b" and a regex lookahead "(?=x)" became "(?==x)"
    let queryNew = replaceOutsideQueryLiterals(qString, (code) => code.replace(/(?<![!<>=~])=(?!=)/g, '=='));

    // Filtrex is patched to support symbolic && and || directly

    // trim queryNew
    queryNew = queryNew.trim();

    // good enough test to insert an options field if we THINK this is an advanced expression using the selectionRegion
    if (isAdvanced && queryNew.includes('selectedRegion')) {
        searchPack.hasSelectedRegionExpression = true;
    }

    // save new query
    searchPack.query = queryNew;
    searchPack.isAdvanced = isAdvanced;
    addErrorToStruct(searchPack, error);

    return error;
}


/**
 * The quoted literals of a query, exactly as filtrex's lexer reads them: a string "..." and a
 * quoted symbol '...' (a field name with odd characters). Neither has escapes -- a backslash is an
 * ordinary character and the first matching quote ends the literal -- so "a\"b" is the string a\
 * followed by code. An unclosed quote matches nothing here (filtrex then reports the error).
 * @constant {RegExp}
 */
const QUERY_LITERAL_REGEX = /"[^"]*"|'[^']*'/g;


/**
 * Applies a rewrite to the code of a query and leaves its quoted literals (QUERY_LITERAL_REGEX)
 * untouched: the query is split into the literals and the pieces between them, the replacer runs
 * on each piece, and everything is joined back in order.
 * @param {string} qString - The query
 * @param {Function} replacer - (codePiece: string) => string
 * @returns {string} The rewritten query
 */
export function replaceOutsideQueryLiterals(qString, replacer) {
    let result = '';
    let pieceStart = 0;
    for (const match of qString.matchAll(QUERY_LITERAL_REGEX)) {
        result += replacer(qString.slice(pieceStart, match.index)) + match[0];
        pieceStart = match.index + match[0].length;
    }
    return result + replacer(qString.slice(pieceStart));
}









/**
 * Evaluates whether an item matches a compiled filtrex query filter.
 * Combines the item data with context variables before evaluation.
 * Caches the item for access by custom filter functions.
 * @param {Object} filtrexCompiled - Pre-compiled filtrex expression object with filterFn and error properties
 * @param {Object} item - Data item to test against the query
 * @param {Object} context - Context variables to inject into the expression scope (e.g., selectedRegion)
 * @returns {boolean} True if item matches the query, false otherwise (including on errors)
 */
export function testAdvancedQueryMatchOnItem(filtrexCompiled, item, context) {
    // If compilation failed, no matches
    if (filtrexCompiled.error || !filtrexCompiled.filterFn) {
        console.log(`Filtrex error with compiled expression; error should have been reported earlier.`)
        return false;
    }

    // we combine the data row (item) with any custom context variables that are exposed to the compiled function
    const itemWithContext = {
        ...item,
        ...context,
        // pass in the item as an object for functions?
        item: item
    };

    // set item in cache so custom functions can access it
    cacheSetItem(item);

    // filtrex returns truthy/falsy values; coerce to boolean
    const matchOutcome = filtrexCompiled.filterFn(itemWithContext);

    // if resultMatches is a string, then search for the string in item like a substring search
    if (typeof matchOutcome === 'string') {
        const matchFn = (value, searchString, field) => value.includes(searchString);
        // lowercased like the field values it is compared with (doesItemContainSubstring): the
        // natural query "Smith" found nothing while "smith" found John Smith
        const doesResultMatch = doesItemContainSubstring(item, matchOutcome.toLowerCase(), ADVANCED_SEARCH_FIELDS, matchFn);
        return doesResultMatch;
    }

    return !!matchOutcome;
}



function doesItemContainSubstring(item, searchString, fields, matchFn) {
    return anyFieldMatches(item, fields, (value, field) => matchFn(value, searchString, field));
}






/**
 * Compiles an advanced query expression using filtrex.
 * Supports expressions like:
 *   - dName == "Smith"
 *   - ptype == "person" and jregion == "MH"
 *   - dName ~= "^Sm" (regex match, case-sensitive; guarded, see makeGuardedRegexMatcher)
 *   - pcatg ~= "Restaurant"
 *   - (ptype == "place" or ptype == "person") and jregion == "BK"
 * Includes built-in string functions (contains, startsWith, endsWith, len, lower, upper)
 * and custom CasebookMap functions (nearPcat, nearRegion, person, place, etc.).
 * @param {Object} searchPack - The search pack containing the query string in searchPack.query
 * @param {Object} searcher - The searcher instance (used to build custom functions)
 * @returns {Object} Compiled query object with:
 *   - filterFn: Function that takes an item and returns truthy if it matches
 *   - error: Error message string if compilation failed, null otherwise
 *   - qstring: The original query string
 */
export function compileAdvancedFiltrexQuery(searchPack, searcher) {
    // build a compiled query structure
    const qstring = searchPack.query;
    const filtrexCompiled = {
        filterFn: null,
        error: null,
        qstring: qstring
    };

    // early errors
    if (!qstring || typeof qstring !== 'string' || !qstring.trim()) {
        filtrexCompiled.error = 'Empty query string';
        return filtrexCompiled;
    }

    // add custom functions to filtrex expression compiler
    const customCbmFunctionDefs = buildCustomCbmFunctions(searcher);
    // one compiled-pattern cache per query, shared by the literal check below and the search itself
    const regexCache = new Map();
    try {
        const extraFunctions = {
            // `x ~= y`: our filtrex calls this entry instead of an unguarded RegExp(y).test(x)
            // (libs/filtrex, a local modification): patterns that can freeze the page are refused
            '~=': makeGuardedRegexMatcher(regexCache),
            // `A includes B`, case-insensitive substring (our filtrex calls this entry, like ~=);
            // a missing field is empty text, not "undefined"
            includes: function(str, substr) {
                return String(missingAsEmptyText(str)).toLowerCase().includes(String(missingAsEmptyText(substr)).toLowerCase());
            },
            // Case-insensitive contains check (a missing field is empty text; any other non-text is no match)
            contains: function(str, substr) {
                str = missingAsEmptyText(str);
                substr = missingAsEmptyText(substr);
                if (typeof str !== 'string' || typeof substr !== 'string') return false;
                return str.toLowerCase().includes(substr.toLowerCase());
            },
            // Case-insensitive starts with
            startsWith: function(str, prefix) {
                str = missingAsEmptyText(str);
                prefix = missingAsEmptyText(prefix);
                if (typeof str !== 'string' || typeof prefix !== 'string') return false;
                return str.toLowerCase().startsWith(prefix.toLowerCase());
            },
            // Case-insensitive ends with
            endsWith: function(str, suffix) {
                str = missingAsEmptyText(str);
                suffix = missingAsEmptyText(suffix);
                if (typeof str !== 'string' || typeof suffix !== 'string') return false;
                return str.toLowerCase().endsWith(suffix.toLowerCase());
            },
            // String length
            len: function(str) {
                if (typeof str !== 'string') return 0;
                return str.length;
            },
            // Convert to lowercase
            lower: function(str) {
                if (typeof str !== 'string') return '';
                return str.toLowerCase();
            },
            // Convert to uppercase
            upper: function(str) {
                if (typeof str !== 'string') return '';
                return str.toUpperCase();
            },
            ...customCbmFunctionDefs
        };

        // Now Compile the expression using filtrex and store in .filterN
        const filterxFn = compileExpression(qstring, extraFunctions);

        // a refused or invalid pattern written straight into the query is reported now, like a
        // syntax error, instead of by the first POI the search tests (with partial results when an
        // "or" let earlier POIs match without reaching the ~=); a pattern computed at run time
        // (dName ~= address) is still checked by the matcher, which throws
        for (const pattern of findLiteralRegexPatternsInQuery(qstring)) {
            const entry = lookupGuardedRegex(regexCache, pattern);
            if (entry.error) {
                filtrexCompiled.error = entry.error;
                return filtrexCompiled;
            }
        }
        filtrexCompiled.filterFn = filterxFn;

    } catch (e) {
        // exception so store error
        filtrexCompiled.error = e.message || String(e);
    }

    return filtrexCompiled;
}



// ==========================================
// REGULAR EXPRESSION MATCH: x ~= "pattern"
// ==========================================

/**
 * Builds the function filtrex calls for `x ~= y`. It matches as the unguarded `RegExp(y).test(x)`
 * did -- case-sensitive, no flags, a number converted to text -- except that each pattern is
 * compiled ONCE per search, a pattern that could freeze the page is refused (see
 * checkRegexPatternSafety), and a missing field (value or pattern) is empty text: test() used to
 * read a missing value as the text "undefined", so hint ~= "^u" matched every place with no hint
 * (a missing pattern was already the empty pattern, which matches everything; a null one read as
 * the text "null"). The search runs the pattern over every POI in one synchronous loop, so before
 * this one bad pattern (^([A-Za-z]+ ?)+$ took 32.8 s on a single 44-character name) hung the tab
 * with no way out.
 * @param {Map<string, {regex?: RegExp, error?: string}>} regexCache - Compiled patterns, per query
 * @returns {Function} (value, pattern) => boolean; throws an Error with a readable message for a
 *   refused or invalid pattern (the advanced searcher shows it as the search's error)
 */
function makeGuardedRegexMatcher(regexCache) {
    return function(value, pattern) {
        const entry = lookupGuardedRegex(regexCache, String(missingAsEmptyText(pattern)));
        if (entry.error) {
            throw new Error(entry.error);
        }
        // (the cached RegExp has no g/y flag, so test() keeps no lastIndex state between POIs)
        return entry.regex.test(missingAsEmptyText(value));
    };
}


/**
 * What a value stands for in the query language's text operators and functions (includes, ~=,
 * contains, startsWith, endsWith, the containsAll/containsAny/fuzzy and ethnicity terms, nearPcat's
 * category): a field the entry does not have (undefined, or null) is EMPTY text. They used to turn
 * it into the text "undefined" (hint includes "in" matched all 2956 places of a world with no hint
 * field) or, in nearPcat, throw. Anything else is returned unchanged, so each caller keeps its own
 * handling of numbers and other non-text values.
 * @param {*} value - A value from the query (a field, a literal, a function's result)
 * @returns {*} '' for undefined or null, otherwise the value itself
 */
export function missingAsEmptyText(value) {
    return (value === undefined || value === null) ? '' : value;
}


/**
 * Returns a pattern's cache entry, compiling and checking it on first use.
 * @param {Map<string, {regex?: RegExp, error?: string}>} regexCache - Compiled patterns, per query
 * @param {string} source - The pattern text
 * @returns {{regex?: RegExp, error?: string}} The compiled pattern, or why it is refused
 * @private
 */
function lookupGuardedRegex(regexCache, source) {
    let entry = regexCache.get(source);
    if (!entry) {
        if (regexCache.size >= SEARCH_CONSTANTS.REGEX_CACHE_MAX_ENTRIES) {
            regexCache.clear();
        }
        entry = compileGuardedRegex(source);
        regexCache.set(source, entry);
    }
    return entry;
}


/**
 * Compiles a `~=` pattern if it is valid and safe to run.
 * @param {string} source - The pattern text
 * @returns {{regex?: RegExp, error?: string}} The compiled pattern, or a readable refusal
 * @private
 */
function compileGuardedRegex(source) {
    const shown = (source.length > 60) ? source.slice(0, 60) + '...' : source;
    const refuse = (reason) => ({ error: `The pattern "${shown}" after ~= ${reason}` });
    if (source.length > SEARCH_CONSTANTS.REGEX_PATTERN_MAX_LENGTH) {
        return refuse(`is too long (${source.length} characters; at most ${SEARCH_CONSTANTS.REGEX_PATTERN_MAX_LENGTH}).`);
    }
    let regex;
    try {
        regex = new RegExp(source);
    } catch (e) {
        return refuse(`is not a valid regular expression (${e.message}).`);
    }
    // (checked after compiling, so the checker only ever reads valid patterns)
    const reason = checkRegexPatternSafety(source);
    return reason ? refuse(reason) : { regex };
}


/**
 * Tells whether a (valid) regular expression could take far too long in the browser's
 * backtracking engine when run over thousands of POIs, and if so why. Refused:
 *   - a group repeated more than once (*, +, {n,}, {n,m} with m > 1) whose body -- nested groups
 *     included -- holds any variable repeat (*, +, ?, {n,}, {n,m}) or a "|": (a+)+, (a|aa)*,
 *     ([A-Za-z]+ ?)+, (a?a)+ (exponential: the engine tries every way to split the text up);
 *   - a backreference (\1 .. \9, \k<name>);
 *   - more repeats than SEARCH_CONSTANTS.REGEX_REPEAT_BUDGET allows (.*.*.*.* is polynomial with
 *     a high power: over a minute for the whole nynoir directory).
 * The length cap is checked by the caller. Deliberately conservative: some harmless shapes are
 * refused too ((a|b)+ -- write [ab]+), and the message says how to rewrite them.
 * @param {string} source - The pattern text (already known to compile)
 * @returns {string|null} The reason (completing "The pattern ... after ~= "), or null if it may run
 */
export function checkRegexPatternSafety(source) {
    // one frame per open group (the outermost is the pattern itself): does its body hold a variable
    // repeat or an alternative?
    const frames = [{ hasVariableRepeat: false, hasAlternative: false }];
    let lastAtomIsRiskyGroup = false;   // the atom a following quantifier applies to
    let hasLastAtom = false;
    let repeatCost = 0;
    let i = 0;
    while (i < source.length) {
        const c = source[i];
        const quantifier = parseRegexQuantifierAt(source, i);
        if (quantifier) {
            if (hasLastAtom) {
                const isVariable = (quantifier.max !== quantifier.min);
                if (isVariable) {
                    frames[frames.length - 1].hasVariableRepeat = true;
                    repeatCost += (quantifier.max - quantifier.min >= 2) ? 3 : 1;
                }
                if (lastAtomIsRiskyGroup && quantifier.max > 1) {
                    return 'repeats a group that itself holds a repeat or a "|" (like (a+)+, (a|aa)* or ([A-Za-z]+ ?)+): such a pattern can take minutes and freeze the page. Rewrite it without the nested repeat (e.g. [ab]+ instead of (a|b)+).';
                }
            }
            hasLastAtom = false;
            lastAtomIsRiskyGroup = false;
            i += quantifier.length;
            continue;
        }
        if (c === '\\') {
            const next = source[i + 1] || '';
            if (/[1-9]/.test(next) || (next === 'k' && source[i + 2] === '<')) {
                return 'uses a backreference (\\1 or \\k<name>), which is not allowed: it can make the search freeze the page.';
            }
            i += 2;
            hasLastAtom = true;
            lastAtomIsRiskyGroup = false;
        } else if (c === '[') {
            // a character class is one atom; inside it nothing is a quantifier or a group (in JS the
            // class ends at the first unescaped "]", even straight after "[" or "[^")
            i++;
            if (source[i] === '^') {
                i++;
            }
            while (i < source.length && source[i] !== ']') {
                i += (source[i] === '\\') ? 2 : 1;
            }
            i++;
            hasLastAtom = true;
            lastAtomIsRiskyGroup = false;
        } else if (c === '(') {
            frames.push({ hasVariableRepeat: false, hasAlternative: false });
            // skip the group's own marker: (?: (?= (?! (?<= (?<! (?<name>
            const marker = /^\(\?(?::|=|!|<=|<!|<[^>]*>)/.exec(source.slice(i));
            i += marker ? marker[0].length : 1;
            hasLastAtom = false;
            lastAtomIsRiskyGroup = false;
        } else if (c === ')') {
            const frame = frames.pop();
            const parent = frames[frames.length - 1];
            // what the group holds, its enclosing groups hold too
            parent.hasVariableRepeat = parent.hasVariableRepeat || frame.hasVariableRepeat;
            parent.hasAlternative = parent.hasAlternative || frame.hasAlternative;
            i++;
            hasLastAtom = true;
            lastAtomIsRiskyGroup = frame.hasVariableRepeat || frame.hasAlternative;
        } else if (c === '|') {
            frames[frames.length - 1].hasAlternative = true;
            i++;
            hasLastAtom = false;
            lastAtomIsRiskyGroup = false;
        } else {
            // a literal character, ".", "^" or "$"
            i++;
            hasLastAtom = true;
            lastAtomIsRiskyGroup = false;
        }
    }
    if (repeatCost > SEARCH_CONSTANTS.REGEX_REPEAT_BUDGET) {
        return 'has too many repeats (*, +, ?, {n,m}): each one multiplies the work, so a pattern like .*.*.*.* can freeze the page. Use at most three *, + or {n,m} (each ? counts as a third of one), or split it into several ~= tests joined with "and".';
    }
    return null;
}


/**
 * Reads a quantifier at a position of a regular expression: *, +, ?, {n}, {n,} or {n,m}, plus a
 * lazy "?" after it. A "{" that does not start one of those is a literal character (as in the
 * browser's own non-unicode parsing).
 * @param {string} source - The pattern text
 * @param {number} i - Position to read at
 * @returns {{min: number, max: number, length: number}|null} The quantifier (max Infinity when
 *   open-ended) and how many characters it spans, or null if there is none at i
 * @private
 */
function parseRegexQuantifierAt(source, i) {
    const c = source[i];
    let min, max, length;
    if (c === '*') {
        [min, max, length] = [0, Infinity, 1];
    } else if (c === '+') {
        [min, max, length] = [1, Infinity, 1];
    } else if (c === '?') {
        [min, max, length] = [0, 1, 1];
    } else if (c === '{') {
        const braces = /^\{(\d+)(,(\d*))?\}/.exec(source.slice(i));
        if (!braces) {
            return null;
        }
        min = Number(braces[1]);
        max = (braces[2] === undefined) ? min : ((braces[3] === '') ? Infinity : Number(braces[3]));
        length = braces[0].length;
    } else {
        return null;
    }
    if (source[i + length] === '?') {
        length++;
    }
    return { min, max, length };
}


/**
 * Finds the patterns written as a string literal straight after a ~= in a query
 * (dName ~= "^Sm" gives ^Sm), so they can be checked when the query is compiled. A ~= inside a
 * quoted literal (QUERY_LITERAL_REGEX, filtrex's own rule) is just text and is skipped with it.
 * @param {string} qstring - The query
 * @returns {string[]} The literal patterns, in order
 * @private
 */
function findLiteralRegexPatternsInQuery(qstring) {
    const patterns = [];
    const tokenRegex = new RegExp(QUERY_LITERAL_REGEX.source + '|~=', 'g');
    let match;
    while ((match = tokenRegex.exec(qstring)) !== null) {
        if (match[0] === '~=') {
            const literal = /^\s*"([^"]*)"/.exec(qstring.slice(tokenRegex.lastIndex));
            if (literal) {
                patterns.push(literal[1]);
            }
        }
    }
    return patterns;
}



/**
 * Builds custom CasebookMap functions for use in filtrex expressions.
 * These functions extend the query language with domain-specific capabilities.
 * @param {Object} searcher - The searcher instance requesting the functions
 * @returns {Object} Dictionary mapping function names to implementations
 * @private
 */
function buildCustomCbmFunctions(searcher) {
    // return an object with custom functions of the form key = callback (see above)
    const poiDataManager = searcher.getMapApp().getPoiDataManager();
    const mapApp = searcher.getMapApp();
    //
    const customFunctions = {
        nearRegion: function(regionTarget) {
            // test if current item neighborhood is adjacent or in the neighborhood specified
            // 
            const flagIncludeSelf = true;
            // (an array: Array.includes is an exact match, where the old comma string's
            // String.includes found region E inside EC/SE)
            const regionList = searcher.getAuxDataManager().getNeighboringRegionList(regionTarget, flagIncludeSelf);
            const currentItem = cacheGetItem();
            const jregion = currentItem.jregion;
            return !!jregion && regionList.includes(jregion);
        },
        nearPcat: function(category, dist=100) {
            // filters for people and poi near other places of a certain category
            // a Map, keyed by the category text the user typed: a plain object read nearPcat("constructor")
            // or nearPcat("__proto__") as a built-in instead of a cached list
            if (!customFunctionDataCache.pCatItems) {
                customFunctionDataCache.pCatItems = new Map();
            }
            // (a missing field as the category is empty text, like nearPcat(""); it threw on toLowerCase)
            return poiDataManager.isNearPcat(cacheGetItem(), missingAsEmptyText(category), convertFeetToNativeMercaturUnits(dist), customFunctionDataCache.pCatItems);
        },
        nearAnchor: function(dist=100) {
            // filters for distance to the current anchor
            const anchorItem = mapApp.getCurrentAnchor().item;
            if (!anchorItem) {
                return false;
            }
            const currentItem = cacheGetItem();
            if (currentItem.geometry === null) {
                return false;
            }
            // only one anchor we can test ourselves here
            const distToAnchor = distanceBetweenTwoItems(anchorItem, currentItem);
            // null = one of the two has no point (an unlisted address): never "near". JavaScript reads
            // null <= x as true, which made nearAnchor() match the whole directory whenever the anchor
            // itself had no location (second code study 12.3, Oct 2026)
            return (distToAnchor !== null) && (distToAnchor <= convertFeetToNativeMercaturUnits(dist));
        },
        nearAnchorRegion: function() {
            // test if current item neighborhood is adjacent or in the neighborhood of the anchor
            // 
            const anchorItem = mapApp.getCurrentAnchor().item;
            const regionTarget = anchorItem?.jregion;
            const flagIncludeSelf = true;
            const regionList = searcher.getAuxDataManager().getNeighboringRegionList(regionTarget, flagIncludeSelf);
            const currentItem = cacheGetItem();
            const jregion = currentItem.jregion;
            return !!jregion && regionList.includes(jregion);
        },
        nearBookmark: function(dist=100) {
            // filters for people and poi near bookmarks
            const resultList =  mapApp.bookmarkSearcher.getListerResults();
            return poiDataManager.isNearResultList(cacheGetItem(), convertFeetToNativeMercaturUnits(dist), resultList);
        },
        //
        livesIn: function() {
            // person that lives in apt or duplex
            const fieldQuery = { htype: { in: ["A", "D"] } };
            return poiDataManager.evaluateFQuery(fieldQuery, cacheGetItem(), false);
        },
        livesApt: function() {
            // person that lives in apt
            const fieldQuery = { htype: "A" };
            return poiDataManager.evaluateFQuery(fieldQuery, cacheGetItem(), false);
        },
        livesDuplex: function() {
            // person that lives in duplex
            const fieldQuery = { htype: "D" };
            return poiDataManager.evaluateFQuery(fieldQuery, cacheGetItem(), false);
        },
        //
        person: function() {
            // matches residents not businesses
            const fieldQuery = { ptype: "person" };
            return poiDataManager.evaluateFQuery(fieldQuery, cacheGetItem(), false);
        },
        place: function() {
            // matches place poi (non-resident, business, etc.)
            const fieldQuery = { or: [{ptype: "yellow"}, {ptype: "poi"}] };
            return poiDataManager.evaluateFQuery(fieldQuery, cacheGetItem(), false);
        },
        poi: function() {
            // matches place poi (non-resident, business, etc.)
            const fieldQuery = { ptype: "poi" };
            return poiDataManager.evaluateFQuery(fieldQuery, cacheGetItem(), false);
        },
        action: function() {
            // special action (like canvas)
            const fieldQuery = { ptype: "action" };
            return poiDataManager.evaluateFQuery(fieldQuery, cacheGetItem(), false);
        },
        //
        vip: function() {
            // poi marked as vip places of interest (should match illustrations on map)
            const fieldQuery = { hint: {contains: "vip"} };
            return poiDataManager.evaluateFQuery(fieldQuery, cacheGetItem(), false);
        },
        //
        bookmarked: function() {
            // is this item bookmarked?
            const fieldQuery = { [FIELD_ISBOOKMARK]: true };
            return poiDataManager.evaluateFQuery(fieldQuery, cacheGetItem(), false);
        },
        customized: function() {
            // does this item have a custom bookmark label or note, etc.
            const fieldQuery = { isCustomized: true };
            return poiDataManager.evaluateFQuery(fieldQuery, cacheGetItem(), false);
        },
        //
        unlisted: function() {
            // does this person have an unlisted address
            const fieldQuery = { address: TEXT_CONSTANTS.UNLISTED_ADDRESS };
            return poiDataManager.evaluateFQuery(fieldQuery, cacheGetItem(), false);
        },
        offmap: function() {
            // is this item listed as being off the map (outside of Manhattan)
            const fieldQuery = { or: [{offmap: 1}, {offmap: 2}] };
            return poiDataManager.evaluateFQuery(fieldQuery, cacheGetItem(), false);
        },
        //
        ethnicity: function(ethnicity) {
            // does this resident match this ethnicity (ethnicity string can be partial substring;
            // a missing field as the term is empty text, not "undefined")
            const fieldQuery = {and: [{pcat: {contains: missingAsEmptyText(ethnicity)}}, {ptype: "person"}]};
            return poiDataManager.evaluateFQuery(fieldQuery, cacheGetItem(), true);
        },
        //
        containsAll: function(...variadicValuesList) {
            // use call to poiDataManager
            const fieldList = ADVANCED_SEARCH_FIELDS;
            const comboOperator = 'all';
            const valueOperator = 'contains';
            // (a missing field as a term is empty text, not "undefined")
            const rawValueList = variadicValuesList.map(missingAsEmptyText);
            const flagCaseInsensitive = true;
            return poiDataManager.evaluateMultiFieldQuery(fieldList, cacheGetItem(), comboOperator, valueOperator, rawValueList, flagCaseInsensitive);
        },
        containsAny: function(...variadicValuesList) {
            // use call to poiDataManager
            const fieldList = ADVANCED_SEARCH_FIELDS;
            const comboOperator = 'any';
            const valueOperator = 'contains';
            // (a missing field as a term is empty text, not "undefined")
            const rawValueList = variadicValuesList.map(missingAsEmptyText);
            const flagCaseInsensitive = true;
            return poiDataManager.evaluateMultiFieldQuery(fieldList, cacheGetItem(), comboOperator, valueOperator, rawValueList, flagCaseInsensitive);
        },
        fuzzy: function(...variadicValuesList) {
            // use call to poiDataManager
            const fieldList = ADVANCED_SEARCH_FIELDS;
            const comboOperator = 'any';
            const valueOperator = 'fuzzy';
            // (a missing field as a term is empty text, not "undefined")
            const rawValueList = variadicValuesList.map(missingAsEmptyText);
            const flagCaseInsensitive = true;
            return poiDataManager.evaluateMultiFieldQuery(fieldList, cacheGetItem(), comboOperator, valueOperator, rawValueList, flagCaseInsensitive);
        },
        fuzzyCompare: function(a,b, threshold) {
            if (typeof a !== 'string' || typeof b !== 'string') return false;
            // (the author's threshold, when given and numeric: this passed an ASSIGNMENT, so the
            // default always replaced it)
            const effectiveThreshold = (typeof threshold === 'number' && isFinite(threshold)) ? threshold : SEARCH_CONSTANTS.DEFAULT_FUZZY_MATCH_THRESHOLD;
            return poiDataManager.fuzzyStringMatchTrueFalse(a, b, effectiveThreshold);
        },
    };
    return customFunctions;
}
















/**
 * Simplifies a filtrex parse error message by removing line number prefix.
 * @param {string} message - The error message from filtrex compilation
 * @returns {string} Simplified error message without "Parse error on line N:" prefix
 */
export function simplifyErrorMessage(message) {
    // Remove text of the form "Parse error on line N:" from the start of message
    return message.replace(/^Parse error on line \d+:\s*/, '');
}




/**
 * Builds a context dictionary for advanced search evaluation.
 * Context variables are injected into the filtrex expression scope.
 * @param {Object} options - Options containing context values
 * @param {string} [options.selectedRegion] - The currently selected region code
 * @returns {Object} Context dictionary with variables accessible in expressions
 */
export function buildContextDictionary(options) {
    // build a context dictionary for advanced searching
    const context = {};

    // pass through selected region
    context.selectedRegion = options['selectedRegion'];
    context.extraCache = {};

    return context;
}